import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Card, Location, Watch } from '../../assests/ExportImageIcon'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { showToast } from '../../componants/toast/toastService'
import loadScript from '../../utils/loadScript'
import { getStripePublishableKey } from '../../config/stripeEnv'
import { extractScheduleArray, mapPaymentScheduleItem } from '../../utils/venueFormat'
import './venueShared.css'

const formatMoney = (value, currency) => {
  if (value === undefined || value === null || value === '') return null
  const amount = Number(value)
  if (Number.isNaN(amount) || amount === 0) return null
  const symbol = !currency || currency === 'INR' ? '₹' : `${currency} `
  return `${symbol}${amount.toLocaleString('en-IN')}`
}

const VenuePayment = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { state: locationState } = useLocation()
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [fareSummaryOpen, setFareSummaryOpen] = useState(true)
  const [paying, setPaying] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(9 * 60 + 29)
  const [stripeReady, setStripeReady] = useState(false)
  const [stripeCardError, setStripeCardError] = useState('')
  // Confirmed live 2026-09-21: booking-confirmation now has a real per-step
  // schedule ({step, title, amount, currency, is_paid}) — used here only to
  // detect an already-fully-paid booking before showing the payment form
  // (best-effort; 404s/errors leave this null and the form still works).
  const [liveSchedule, setLiveSchedule] = useState(null)
  const stripeRef = useRef(null)
  const elementsRef = useRef(null)
  const paymentElementRef = useRef(null)
  const mountTokenRef = useRef(0)

  useEffect(() => {
    if (secondsLeft <= 0) return
    const timerId = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000)
    return () => clearTimeout(timerId)
  }, [secondsLeft])

  useEffect(() => {
    if (!id) return
    setLoading(true)
    setLoadError(false)
    service
      .get(endpoints.venues.proposalConfirmBookingDetails.replace('{proposal_id}', id), {}, { silent: true })
      .then(({ data }) => {
        const body = data?.data ?? data ?? null
        if (!body) throw new Error('empty response')
        setDetails(body)
      })
      .catch(() => {
        setDetails(null)
        setLoadError(true)
      })
      .finally(() => setLoading(false))

    service
      .get(endpoints.venues.bookingConfirmation.replace('{proposal_id}', id), {}, { silent: true })
      .then(({ data }) => {
        const body = data?.data ?? data ?? null
        const schedule = extractScheduleArray(body)
        if (schedule.length) setLiveSchedule(schedule.map(mapPaymentScheduleItem))
      })
      .catch(() => {
        // Best-effort — a fresh proposal with no payment yet may 404 here.
      })
  }, [id])

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0')
  const seconds = String(secondsLeft % 60).padStart(2, '0')

  // Confirmed live 2026-09-21: venue switched from Razorpay to Stripe — the
  // `action` (accept) call on venueConfirmBooking.jsx now returns the
  // PaymentIntent directly ({client_secret, payment_intent_id,
  // publishable_key, gateway: "stripe", amount, currency}), carried here via
  // nav state. No separate paymentCheckout call is needed for Stripe.
  const clientSecret = locationState?.clientSecret || ''
  const stripeKey = clientSecret ? (locationState?.publishableKey || getStripePublishableKey()) : ''

  const destroyStripeElement = () => {
    if (paymentElementRef.current) {
      try {
        paymentElementRef.current.unmount()
      } catch {
        // ignore unmount races
      }
      paymentElementRef.current = null
    }
    elementsRef.current = null
    setStripeReady(false)
  }

  // Mounts Stripe's Payment Element against the client_secret we already
  // have — same pattern as hotelPayment.jsx's "booking API already created
  // PaymentIntent" path. This effect runs before the loading/error early
  // returns below (Rules of Hooks), so on its first pass `loading` is still
  // true and the #venue-payment-element div hasn't rendered yet — bail out
  // and let the `loading` dependency re-fire this once the real panel (and
  // its mount div) actually exists.
  useEffect(() => {
    if (loading || loadError || !clientSecret || !stripeKey) {
      destroyStripeElement()
      return undefined
    }

    let cancelled = false
    const token = ++mountTokenRef.current

    const mountStripe = async () => {
      setStripeReady(false)
      setStripeCardError('')
      try {
        const Stripe = await loadScript('https://js.stripe.com/v3/', 'Stripe')
        if (cancelled || token !== mountTokenRef.current) return
        stripeRef.current = Stripe(stripeKey)

        destroyStripeElement()
        if (cancelled || token !== mountTokenRef.current) return

        await new Promise((r) => window.setTimeout(r, 50))
        if (cancelled || token !== mountTokenRef.current) return

        const mountEl = document.getElementById('venue-payment-element')
        if (!mountEl) {
          if (!cancelled) setStripeCardError('Stripe form container not ready. Refresh and try again.')
          return
        }
        mountEl.innerHTML = ''
        const elements = stripeRef.current.elements({
          clientSecret,
          appearance: { theme: 'stripe', variables: { colorPrimary: '#026D4D', borderRadius: '12px' } },
        })
        elementsRef.current = elements
        const paymentElement = elements.create('payment', { layout: { type: 'tabs', defaultCollapsed: false } })
        paymentElement.mount('#venue-payment-element')
        paymentElement.on('change', (event) => {
          if (!cancelled) setStripeCardError(event.error ? event.error.message : '')
        })
        paymentElement.on('ready', () => {
          if (!cancelled && token === mountTokenRef.current) setStripeReady(true)
        })
        paymentElementRef.current = paymentElement
      } catch (error) {
        if (!cancelled) setStripeCardError(error?.message || 'Failed to load Stripe payment form.')
      }
    }

    mountStripe()

    return () => {
      cancelled = true
      destroyStripeElement()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret, stripeKey, loading, loadError])

  const handlePayClick = async () => {
    if (paying) return
    if (!clientSecret) {
      showToast('Payment session expired. Please go back and try again.', 'error')
      return
    }
    if (!stripeRef.current || !elementsRef.current) {
      showToast('Payment form is still loading. Please wait a moment.', 'error')
      return
    }
    setPaying(true)
    try {
      const result = await stripeRef.current.confirmPayment({
        elements: elementsRef.current,
        redirect: 'if_required',
        confirmParams: { return_url: `${window.location.origin}/venue-booking-success/${id}` },
      })
      if (result.error) throw new Error(result.error.message || 'Payment failed.')
      const intent = result.paymentIntent
      if (!intent || !['succeeded', 'processing', 'requires_capture'].includes(intent.status)) {
        throw new Error(`Payment status: ${intent?.status || 'unknown'}`)
      }

      // Confirmed live 2026-09-21: verify 404s ("Payment record not found")
      // if called immediately — Stripe confirms client-side before the
      // backend's own webhook has written its payment record, a race. Retry
      // a few times with a short delay rather than treating the first 404 as
      // real failure. Per Jatin: booking-confirmation (fetched on the
      // success page) must only ever be reached once verify has genuinely
      // succeeded — don't navigate there on a failed/exhausted verify.
      let paymentStatus = null
      let verified = false
      for (let attempt = 0; attempt < 4 && !verified; attempt += 1) {
        if (attempt > 0) await new Promise((r) => window.setTimeout(r, 1200))
        try {
          const verifyResponse = await service.post(endpoints.venues.paymentVerify.replace('{proposal_id}', id), {
            gateway: 'stripe',
            payment_intent_id: intent.id,
          }, { silent: true })
          paymentStatus = verifyResponse?.data?.data ?? null
          verified = true
        } catch {
          // retry
        }
      }

      if (!verified) {
        showToast('Payment succeeded, but confirmation is taking longer than expected. Please check My Trips shortly.', 'error')
        return
      }

      navigate(`/venue-booking-success/${id}`, { state: { bookingId: locationState?.bookingId ?? null, paymentStatus } })
    } catch (error) {
      showToast(error?.message || 'Payment failed. Please try again.', 'error')
    } finally {
      setPaying(false)
    }
  }

  if (loading) {
    return (
      <section className='paymentPart commonTopPadding commonBottomPadding'>
        <div className='container'>
          <p className='hotelResultsEmpty'>Loading payment details...</p>
        </div>
      </section>
    )
  }

  if (loadError || !details) {
    return (
      <section className='paymentPart commonTopPadding commonBottomPadding'>
        <div className='container'>
          <p className='hotelResultsEmpty'>We couldn&apos;t load your booking details. Please go back and try again.</p>
          <button type='button' className='commonBtn' onClick={() => navigate('/venue-booking-confirm')}>Back to Proposals</button>
        </div>
      </section>
    )
  }

  const venueDetails = details.venue_details || {}
  const venueBooking = {
    name: venueDetails.name || '',
    location: venueDetails.location || '',
  }

  const bookingSummary = [
    { label: 'Event Type:', value: venueDetails.event_type || '—' },
    { label: 'Date:', value: venueDetails.date || '—' },
    { label: 'Time Slot:', value: venueDetails.time_slot || '—' },
    { label: 'Guests:', value: String(venueDetails.guests ?? '—') },
  ]

  // confirm-booking-details always returns the ORIGINAL full schedule, with
  // no indication of which step is already paid.
  const staticSchedule = Array.isArray(details.payment_schedule)
    ? details.payment_schedule.map(mapPaymentScheduleItem)
    : []
  // Prefer the live schedule (booking-confirmation, real per-step is_paid
  // confirmed live 2026-09-21) when it loaded — the only source that knows
  // about a step already paid (including a vendor-marked cash payment).
  const schedule = liveSchedule || staticSchedule
  const nextPendingStep = liveSchedule ? (liveSchedule.find((s) => !s.isPaid) || null) : null
  const allInstallmentsPaid = !!liveSchedule?.length && liveSchedule.every((s) => s.isPaid)

  // Fallback chain: the success page's own payment/verify-derived nextStep
  // (carried via nav state when arriving via "Pay Next Installment") takes
  // priority, then the first installment amount, then the overall fare total.
  const nextStepFromState = locationState?.nextStep || null
  const fareBreakupSource = details.fare_breakup || {}
  // `payAmount` is the exact amount the Stripe PaymentIntent (from the
  // accept response) will actually charge — the most authoritative source
  // when present, ahead of the other fallbacks below.
  const amountDueNow = formatMoney(locationState?.payAmount, locationState?.payCurrency)
    || formatMoney(nextPendingStep?.amount, nextPendingStep?.currency)
    || nextStepFromState?.amount
    // `first_installment_amount` (confirmed live 2026-09-21) — not
    // payment_schedule[0], per Jatin.
    || (!nextPendingStep && !nextStepFromState ? formatMoney(details.first_installment_amount, fareBreakupSource.currency) : null)
    // Confirmed live 2026-09-21: the field is `payable_amount`, not `total_amount`.
    || formatMoney(fareBreakupSource.payable_amount, fareBreakupSource.currency)
    || '—'
  const dueLabel = nextPendingStep?.title || nextStepFromState?.title || null

  return (
    <>
        <section className='paymentPart commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='row g-4'>
                    <div className='col-md-12'>
                        <div className='time bgWhite paddingCommon20 borderRadius40 commonBorder'>
                            <span className='timePill'>
                                <img src={Watch} alt="" />
                                {minutes} : {seconds}
                            </span>
                        </div>
                    </div>
                    <div className='col-md-12'>
                        <div className='paymentSec allBookingDetail'>
                            <div className='onlineCrad allBokkingInfo'>
                                <div className='bsSectionTitle'>Payment</div>
                                {allInstallmentsPaid ? (
                                    <p className='bsFareNote'>All installments for this booking have already been paid.</p>
                                ) : !clientSecret ? (
                                    <p className='bsFareNote'>Your payment session has expired or this page was opened directly. Please go back and tap Pay again.</p>
                                ) : (
                                    <p className='bsFareNote'>Enter your card details to complete your booking.</p>
                                )}

                                {!allInstallmentsPaid && clientSecret && (
                                    <div className='paymentMethodBox'>
                                        <div className='paymentMethodList'>
                                            <div className='paymentMethodItem active'>
                                                <span className='paymentMethodIcon'>
                                                    <img src={Card} alt='Stripe' />
                                                </span>
                                                <div>
                                                    <div className='paymentMethodTitle'>Card / Stripe</div>
                                                    <div className='paymentMethodSub'>International cards</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className='paymentMethodPanel'>
                                            <div className='bsSectionTitle'>Pay via Stripe</div>
                                            {dueLabel && <p className='paymentQrDesc'>Paying: {dueLabel}</p>}
                                            <div className='paymentStripeCardWrap'>
                                                <div id='venue-payment-element' className='paymentStripeCard' />
                                                {stripeCardError && <p className='bsFareNote'>{stripeCardError}</p>}
                                            </div>

                                            <button
                                                type='button'
                                                className='commonBtn w-100'
                                                disabled={paying || !stripeReady}
                                                onClick={handlePayClick}
                                            >
                                                {paying ? 'Processing...' : !stripeReady ? 'Loading...' : `Pay ${amountDueNow}`}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {!allInstallmentsPaid && !clientSecret && (
                                    <button type='button' className='commonBtn w-100' onClick={() => navigate(`/venue-confirm-booking/${id}`)}>
                                        Back to Booking Details
                                    </button>
                                )}
                            </div>
                            <div className='paymentSidebar bookingSidebar'>
                                <div className='bsSection bgWhite paddingCommon20 borderRadius20 commonBorder'>
                                    <div className='fareDetailsHead' onClick={() => setFareSummaryOpen(!fareSummaryOpen)}>
                                        <div className='bsSectionTitle'>Fare Summary</div>
                                        <span className={`fareCollapseIcon ${fareSummaryOpen ? 'open' : ''}`}>&#8963;</span>
                                    </div>

                                    {fareSummaryOpen && (
                                        <>
                                            <div className='bsFareRow bsFareRow--total mt-3'>
                                                <span>Amount To Be Paid</span>
                                                <span>{allInstallmentsPaid ? '₹0' : amountDueNow}</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className='bsSection'>
                                    <div className='bsBox'>
                                        <div className='hotelCard__topRow'>
                                            <div>
                                                <div className='hotelCard__name'>{venueBooking.name}</div>
                                                <div className='hotelCard__location mt-2'>
                                                    <img src={Location} alt="" />
                                                    <div className='hotelCard__locationName'>{venueBooking.location}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className='bsFareDivider' />

                                        {bookingSummary.map((item) => (
                                            <div className='bsFareRow mt-2' key={item.label}>
                                                <span>{item.label}</span>
                                                <span>{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Schedule</div>
                                    <div className='bsBox'>
                                        {schedule.map((item) => (
                                            <div className='venueScheduleRow' key={item.step}>
                                                <div className='venueScheduleRow__label'>
                                                    <span className={`venueScheduleRow__step ${item.isPaid ? '' : 'venueScheduleRow__step--inactive'}`}>{item.step}</span>
                                                    <div>
                                                        <div className='venueScheduleRow__title'>{item.title}</div>
                                                        <div className={`venueScheduleRow__due ${item.isPaid ? 'venueScheduleRow__due--paid' : ''}`}>
                                                            {item.isPaid
                                                                ? `Paid${item.paymentMethodLabel ? ` via ${item.paymentMethodLabel}` : ''}`
                                                                : item.dueText}
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className='venueScheduleRow__value'>{formatMoney(item.amount, item.currency) || '—'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </>
  )
}

export default VenuePayment
